// Step 3: Pick Cells - Simple list of cells meeting requirements

import { h } from 'preact'
import { useState, useEffect, useMemo } from 'preact/hooks'
import { performanceCells } from '../../state/benchmarkingWorkflowState.js'
import { addToCompare, removeFromCompare, isInCompare, compareCells } from '../../state/filterStore.js'
import { getSignedPictureUrl } from '../../modules/dataService.js'
import { IconPlus, IconX, IconTable, IconChevronUp, IconChevronDown } from '../shared/Icons.jsx'

export default function Step3CellList() {
  const [imageUrls, setImageUrls] = useState({})
  const [sortKey, setSortKey] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  
  // Get cells that meet requirements from Step 2
  const baseCells = performanceCells.value || []
  
  // Subscribe to compareCells signal for reactivity (accessing .value triggers re-render on change)
  const compareList = compareCells.value
  
  // Sort cells based on current sort key and direction
  const cells = useMemo(() => {
    const sorted = [...baseCells].sort((a, b) => {
      let aVal, bVal
      
      switch (sortKey) {
        case 'name':
          aVal = (a.name || a.product_name || '').toLowerCase()
          bVal = (b.name || b.product_name || '').toLowerCase()
          break
        case 'manufacturer':
          aVal = (a.manufacturer || a.general?.manufacturer || '').toLowerCase()
          bVal = (b.manufacturer || b.general?.manufacturer || '').toLowerCase()
          break
        case 'format':
          aVal = (a.format || a.mechanical?.format || '').toLowerCase()
          bVal = (b.format || b.mechanical?.format || '').toLowerCase()
          break
        case 'energy':
          aVal = a.electrical?.E_grav_Wh_per_kg || 0
          bVal = b.electrical?.E_grav_Wh_per_kg || 0
          break
        case 'power':
          aVal = a.electrical?.P_grav_W_per_kg || 0
          bVal = b.electrical?.P_grav_W_per_kg || 0
          break
        case 'efficiency':
          aVal = a.electrical?.eta_dc_pct || 0
          bVal = b.electrical?.eta_dc_pct || 0
          break
        default:
          aVal = 0
          bVal = 0
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
    })
    
    return sorted
  }, [baseCells, sortKey, sortDirection])
  
  // Handle column header click for sorting
  const handleSort = (key) => {
    if (sortKey === key) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      // New column - default to ascending for text, descending for numbers
      setSortKey(key)
      setSortDirection(['energy', 'power', 'efficiency'].includes(key) ? 'desc' : 'asc')
    }
  }
  
  // Render sort indicator
  const SortIndicator = ({ columnKey }) => {
    if (sortKey !== columnKey) return null
    return sortDirection === 'asc' 
      ? <IconChevronUp size={14} />
      : <IconChevronDown size={14} />
  }
  
  // Load cell images
  useEffect(() => {
    cells.forEach(async (cell) => {
      const cellId = cell.product_id || cell.id
      if (!imageUrls[cellId]) {
        try {
          const signedUrl = await getSignedPictureUrl(cellId, 'website_picture_cell1.png')
          setImageUrls(prev => ({ ...prev, [cellId]: signedUrl }))
        } catch(err) {
          console.warn(`Failed to load image for ${cellId}:`, err)
        }
      }
    })
  }, [cells])
  
  const toggleCompare = (cellId) => {
    if (isInCompare(cellId)) {
      // Remove from compare using filterStore function
      removeFromCompare(cellId)
    } else {
      addToCompare(cellId)
    }
  }
  
  const addAllToCompare = () => {
    // Add first 5 cells to compare (max limit)
    const cellsToAdd = cells.slice(0, 5)
    cellsToAdd.forEach(cell => {
      const cellId = cell.product_id || cell.id
      if (!isInCompare(cellId)) {
        addToCompare(cellId)
      }
    })
  }
  
  return (
    <div className="step-cell-list">
      <div className="step-cell-list-card">
        {/* Header */}
        <div className="cell-list-header" data-tour="benchmarking-step3">
          <div className="cell-list-title">
            <IconTable size={24} />
            <h2>Cells Meeting Your Requirements</h2>
          </div>
          <div className="cell-list-subtitle">
            {cells.length} cell{cells.length !== 1 ? 's' : ''} match your performance criteria. 
            Add cells to compare for detailed analysis.
          </div>
          {cells.length > 0 && (
            <button 
              className="add-all-btn"
              onClick={addAllToCompare}
            >
              <IconPlus size={16} />
              <span>Add Top 5 to Compare</span>
            </button>
          )}
        </div>
      
        {/* Table */}
        <div className="cell-list-table-wrapper" data-tour="benchmarking-results-table">
          <table className="cell-list-table">
            <thead>
              <tr>
                <th className="col-image">Image</th>
                <th className="col-manufacturer sortable" onClick={() => handleSort('manufacturer')}>
                  <span>Manufacturer</span>
                  <SortIndicator columnKey="manufacturer" />
                </th>
                <th className="col-cell sortable" onClick={() => handleSort('name')}>
                  <span>Cell Name</span>
                  <SortIndicator columnKey="name" />
                </th>
                <th className="col-format sortable" onClick={() => handleSort('format')}>
                  <span>Format</span>
                  <SortIndicator columnKey="format" />
                </th>
                <th className="col-energy sortable" onClick={() => handleSort('energy')}>
                  <span>Energy</span>
                  <SortIndicator columnKey="energy" />
                </th>
                <th className="col-power sortable" onClick={() => handleSort('power')}>
                  <span>Power</span>
                  <SortIndicator columnKey="power" />
                </th>
                <th className="col-efficiency sortable" onClick={() => handleSort('efficiency')}>
                  <span>Efficiency</span>
                  <SortIndicator columnKey="efficiency" />
                </th>
                <th className="col-action" data-tour="benchmarking-compare-action">Compare</th>
              </tr>
            </thead>
            <tbody>
              {cells.map((cell) => {
                const cellId = cell.product_id || cell.id
                const inCompare = compareList.includes(cellId)
                
                return (
                  <tr key={cellId} className={inCompare ? 'in-compare' : ''}>
                    <td className="col-image">
                      <div className="cell-image">
                        {imageUrls[cellId] ? (
                          <img 
                            src={imageUrls[cellId]}
                            alt={cell.name || cell.product_name}
                            loading="lazy"
                          />
                        ) : (
                          <div className="cell-image-placeholder">
                            <span>–</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="col-manufacturer">
                      {cell.manufacturer || cell.general?.manufacturer || '–'}
                    </td>
                    <td className="col-cell">
                      <div className="cell-info">
                        <div className="cell-name">{cell.name || cell.product_name}</div>
                      </div>
                    </td>
                    <td className="col-format">
                      {cell.format || cell.mechanical?.format || '–'}
                    </td>
                    <td className="col-energy">
                      {cell.electrical?.E_grav_Wh_per_kg?.toFixed(0) || '–'}
                      <span className="unit">Wh/kg</span>
                    </td>
                    <td className="col-power">
                      {cell.electrical?.P_grav_W_per_kg?.toFixed(0) || '–'}
                      <span className="unit">W/kg</span>
                    </td>
                    <td className="col-efficiency">
                      {cell.electrical?.eta_dc_pct?.toFixed(1) || '–'}
                      <span className="unit">%</span>
                    </td>
                    <td className="col-action">
                      <button
                        className={`compare-toggle-btn ${inCompare ? 'active remove' : ''}`}
                        onClick={() => toggleCompare(cellId)}
                        title={inCompare ? 'Remove from compare' : 'Add to compare'}
                      >
                        {inCompare ? (
                          <>
                            <IconX size={16} />
                            <span>Remove</span>
                          </>
                        ) : (
                          <>
                            <IconPlus size={16} />
                            <span>Add</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          
          {cells.length === 0 && (
            <div className="cell-list-empty">
              <p>No cells match your requirements. Try adjusting the performance criteria in Step 2.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
